import { useStore } from '@nanostores/react';
import { useLocation } from '@remix-run/react';
import { computed, map, type MapStore, type ReadableAtom, type WritableAtom } from 'nanostores';
import { createContext, useContext, useEffect, useRef, type PropsWithChildren } from 'react';
import { ConnectToGithubDialog } from '~/components/github/ConnectToGithubDialog';
import { getGithubToken } from '~/lib//github-api';
import { useGithubToken, type GetGithubToken } from '~/lib/client-github';
import { getExplicitBranch } from '~/lib/github';
import { userStore, type UserStore } from '~/lib/stores/user';
import type { ProjectGithubIntegration, Target } from '~/types/github';
import type { UserData } from '~/types/user';
import { EventEmitter } from '~/utils/event-emitter';
import { useInitRef } from '~/utils/init-ref';
import { waitUntil } from '~/utils/nanostore';
import { unreachable } from '~/utils/unreachable';

export enum CloneStateKind {
  Ignore,
  Pending,
  Clean,
  Done,
  Error,
}

type CloneState =
  | { kind: Exclude<CloneStateKind, CloneStateKind.Error> }
  | { kind: CloneStateKind.Error; message?: string };

export interface GithubData {
  integration?: Target;
  status: 'pending' | 'success';
  clone: CloneState;
  branchOverride: boolean;
  username: string | null;
  connected: boolean | null;
  connecting: 'active' | 'visible' | false;
  isDropdownVisible: boolean;
}

interface GithubStoreEvents {
  cloned: [Error | undefined, boolean];
  connected: [];
}

class GithubStore extends EventEmitter<GithubStoreEvents> {
  // FIXME: use private fields once we update storybook: https://github.com/stackblitz/bolt/actions/runs/15270782493/job/42945696944
  private _store: MapStore<GithubData>;
  private _disposables: Array<() => void> = [];
  private _username: ReadableAtom<string | null>;
  private _target: ReadableAtom<Target | undefined>;
  private _connection: ReadableAtom<{ repo: boolean; account: boolean | null }>;

  constructor(
    initial: GithubData,
    private _getGithubToken: GetGithubToken,
  ) {
    super();

    const store = map<GithubData>(initial);

    this._store = store;

    this._username = computed(this._store, ({ username }) => username);

    this._target = computed([store], ({ username, integration, connected }) => {
      if (username && integration && connected) {
        return integration;
      }

      return undefined;
    });

    this._connection = computed([store], (value) => ({
      repo: Boolean(value.integration),
      account: value.connected,
    }));
  }

  get username() {
    return this._username;
  }

  get state(): ReadableAtom<GithubData> {
    return this._store;
  }

  get target() {
    return this._target;
  }

  get connection() {
    return this._connection;
  }

  async connected() {
    const { connected } = this._store.get();

    if (connected != null) {
      return connected;
    }

    const state = await waitUntil(this._store, ({ connected }) => connected != null);

    return state.connected as boolean;
  }

  branchSwitch() {
    const { integration, branchOverride } = this._store.get();

    return branchOverride && integration ? { branchName: integration.branchName } : undefined;
  }

  init(userStore: UserStore['user']) {
    const disposeUsernameCheck = subscribeToUsername(this._store, userStore);
    this._disposables.push(disposeUsernameCheck);

    const needsClone = this._store.get().clone.kind !== CloneStateKind.Ignore;

    waitUntil(this._store, (data) => cloningIsFinished(data.clone)).then(({ clone }) => {
      this.dispatch('cloned', [
        clone.kind === CloneStateKind.Error ? new Error(clone.message ?? 'Error syncing with repository') : undefined,
        needsClone,
      ]);
    });

    const disposeConnectionChecks = this._store.subscribe(({ connected, connecting }, previous) => {
      if (connected == null && (!previous || previous.connected != null)) {
        if (connecting && import.meta.env.DEV) {
          unreachable('Connecting to GH should not reset connection status');
        }

        this._checkConnection();
      }
    });

    const disposeConnected = computed(this._store, ({ connected }) => connected).listen((connected) => {
      if (connected) {
        this.dispatch('connected', []);
      }
    });

    if (this._store.get().integration) {
      this.connectToGithub();
    }

    this._disposables.push(disposeConnectionChecks);
    this._disposables.push(disposeConnected);
  }

  destroy() {
    for (const dispose of this._disposables) {
      dispose();
    }
  }

  connectToRepo = (integration: Target) => {
    const current = this._store.get();

    this._store.set({
      ...current,
      integration: { ...current.integration, ...integration },
    });
  };

  changeDropdownVisibility = (visible?: boolean) => {
    const { connected, connecting, integration, isDropdownVisible } = this._store.get();

    if (connected && !connecting && integration != null) {
      this._store.setKey('isDropdownVisible', visible ?? !isDropdownVisible);
    }
  };

  connectToGithub = async (loggedInUser?: UserData) => {
    // if we just logged in, make sure the state is up to date
    if (loggedInUser) {
      await waitUntil(this._store, (value) => value.username === githubUsername(loggedInUser));
    }

    await waitUntil(this._store, (value) => value.connected != null);

    if (this._store.get().connected) {
      return true;
    }

    this._store.setKey('connecting', 'visible');

    await waitUntil(this._store, (value) => !value.connecting);

    return this._store.get().connected;
  };

  cancelConnectToGithub = () => {
    this._store.setKey('connecting', false);
  };

  checkGithubConnection = () => {
    this._store.setKey('connecting', 'active');

    this._checkConnection().finally(() => {
      this._store.setKey('connecting', false);
    });
  };

  private async _checkConnection() {
    const token = await getGithubToken();

    this._store.setKey('connected', token != null);
  }

  setSyncStatus = (state: Partial<Pick<GithubData, 'status' | 'clone'>>) => {
    this._store.set({ ...this._store.get(), ...state });
  };
}

const GithubContext = createContext(
  new GithubStore(
    {
      status: 'success',
      clone: { kind: CloneStateKind.Ignore },
      username: null,
      branchOverride: false,
      connected: null,
      connecting: false,
      isDropdownVisible: false,
    },
    async () => undefined,
  ),
);

export function GithubIntegration({
  children,
  initial,
  onCloned,
  initialUser,
}: PropsWithChildren<{
  initial?: ProjectGithubIntegration;
  onCloned: (error: any, success: boolean) => void;
  initialUser: UserData | undefined;
}>) {
  const location = useLocation();

  const getGithubToken = useGithubToken();

  const storeRef = useInitRef(() => {
    const search = new URLSearchParams(location.search);

    const branch = getExplicitBranch(search);

    const username = githubUsername(initialUser);

    return new GithubStore(
      {
        integration: initial,
        status: 'success',
        clone: { kind: initial?.initialized ? CloneStateKind.Pending : CloneStateKind.Ignore },
        branchOverride: branch != null,
        connecting: false,
        connected: username ? null : false,
        username: username ?? null,
        isDropdownVisible: false,
      },
      getGithubToken,
    );
  });

  const store = storeRef.current;

  if (import.meta.env.DEV) {
    Object.assign(globalThis, { githubStore: store });
  }

  const onClonedRef = useRef(onCloned);

  useEffect(() => {
    onClonedRef.current = onCloned;
  });

  useEffect(() => {
    const disposeCloned = store.on('cloned', (event) => {
      const [error, success] = event.detail;

      onClonedRef.current(error, success);
    });

    store.init(userStore.user);

    return () => {
      disposeCloned();
      store.destroy();
    };
  }, []);

  const { connecting } = useStore(store.state);

  const user = useStore(userStore.user);

  return (
    <GithubContext.Provider value={store}>
      {children}
      <ConnectToGithubDialog
        user={user}
        open={connecting === 'visible'}
        onSuccess={store.checkGithubConnection}
        onClose={store.cancelConnectToGithub}
      />
    </GithubContext.Provider>
  );
}

export function useGithubStore() {
  const store = useContext(GithubContext);

  return store;
}

function githubUsername(user: UserData | undefined) {
  return user?.externalAccounts.find((account) => account.provider === 'github')?.username;
}

function cloningIsFinished(state: CloneState) {
  return state.kind !== CloneStateKind.Pending;
}

function subscribeToUsername(store: WritableAtom<GithubData>, userStore: UserStore['user']) {
  return computed([userStore], githubUsername).listen((username) => {
    store.set({
      ...store.get(),
      username: username ?? null,
      connected: username ? null : false,
    });
  });
}
